/**
 * Questo file definisce il componente Navbar.
 * - È la barra di navigazione principale dell'applicazione, visibile in quasi tutte le pagine.
 * - Gestisce dinamicamente il suo aspetto (trasparente o solido) in base allo scroll e alla pagina corrente.
 * - Mostra link di navigazione diversi a seconda che l'utente sia autenticato o meno.
 * - Se l'utente è autenticato, mostra un menu a tendina con opzioni per la dashboard, l'account e il logout.
 * - Se non è autenticato, mostra un pulsante per accedere.
 * - Utilizza 'NavLink' di React Router per evidenziare il link della pagina attiva.
 * - Gestisce la logica di logout dispatchando un'azione Redux.
 */

import React, { useState, useEffect } from 'react';
// Importa i componenti di React Router per la navigazione e la gestione della rotta attiva.
import { NavLink, Link, useLocation, useNavigate } from 'react-router-dom';
// Importa i ganci di React-Redux per interagire con lo store.
import { useSelector, useDispatch } from 'react-redux';
// Importa una utility per unire dinamicamente le classi CSS.
import classNames from 'classnames';
// Importa l'azione di logout dal slice di autenticazione.
import { logoutUser } from '../redux/authSlice';
import './Navbar.css';

/**
 * Componente funzionale Navbar.
 * @returns {React.ReactElement} - Il JSX che renderizza la barra di navigazione.
 */
export default function Navbar() {
    // Stato per tracciare se l'utente ha scrollato la pagina.
    const [scrolled, setScrolled] = useState(false);
    // Hook di React Router per ottenere informazioni sulla rotta corrente (es. pathname).
    const { pathname } = useLocation();
    // Hook di Redux per accedere allo stato di autenticazione e ai dati dell'utente.
    const { isAuthenticated, user } = useSelector((state) => state.auth);
    // Hook di Redux per ottenere la funzione dispatch.
    const dispatch = useDispatch();
    // Hook di React Router per la navigazione programmatica.
    const navigate = useNavigate();

    /**
     * Gestore per l'azione di logout.
     * - Fa il dispatch dell'azione asincrona 'logoutUser'.
     * - Dopo che il logout è completato, reindirizza l'utente alla homepage.
     */
    const handleLogout = () => {
        dispatch(logoutUser()).then(() => {
            navigate('/');
        });
    };

    // Effetto che si attiva al montaggio del componente per gestire lo scroll.
    useEffect(() => {
        // Funzione che aggiorna lo stato 'scrolled' se la posizione di scroll verticale è maggiore di 50px.
        const handleScroll = () => setScrolled(window.scrollY > 50);
        // Aggiunge l'event listener per lo scroll alla finestra.
        window.addEventListener('scroll', handleScroll);
        // Chiama la funzione una volta al montaggio per impostare lo stato iniziale corretto.
        handleScroll();
        // Funzione di pulizia: rimuove l'event listener quando il componente viene smontato.
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    // Determina se la navbar deve essere trasparente.
    // È trasparente solo se l'utente non ha scrollato E si trova sulla homepage ('/').
    const isTransparent = !scrolled && pathname === '/';

    return (
        // L'elemento <nav> principale.
        // Le classi vengono applicate dinamicamente con 'classNames':
        // - 'navbar-transparent' se 'isTransparent' è true.
        // - 'navbar-solid' se 'isTransparent' è false.
        <nav
            className={classNames('navbar navbar-custom fixed-top navbar-expand-lg navbar-dark', {
                'navbar-transparent': isTransparent,
                'navbar-solid': !isTransparent,
            })}
        >
            <div className="container">
                {/* Brand/Logo dell'applicazione che riporta alla homepage. */}
                <Link className="navbar-brand fw-bold fs-4" to="/">
                    <i className="bi bi-lightbulb-fill me-2"></i>FlashMind
                </Link>
                {/* Pulsante "hamburger" per il menu su schermi piccoli. */}
                <button className="navbar-toggler" type="button" data-bs-toggle="collapse" data-bs-target="#navMenu">
                    <span className="navbar-toggler-icon"></span>
                </button>
                {/* Contenitore del menu che diventa collassabile su mobile. */}
                <div className="collapse navbar-collapse" id="navMenu">
                    <ul className="navbar-nav ms-auto align-items-center">
                        {/* Link alla Home. 'NavLink' aggiunge automaticamente la classe 'active' quando la rotta corrisponde. 'end' assicura che il match sia esatto. */}
                        <li className="nav-item">
                            <NavLink className="nav-link" to="/" end>Home</NavLink>
                        </li>
                        {/* Link alle flashcard pubbliche. */}
                        <li className="nav-item">
                            <NavLink className="nav-link" to="/public">Flashcard Pubbliche</NavLink>
                        </li>
                        
                        {/* Renderizzazione condizionale basata sull'autenticazione. */}
                        {isAuthenticated ? (
                            // Se l'utente è loggato, mostra il menu a tendina.
                            <li className="nav-item dropdown ms-lg-2">
                                <button className="nav-link dropdown-toggle btn" type="button" data-bs-toggle="dropdown" aria-expanded="false">
                                    {user?.nome} {/* Mostra il nome dell'utente. */}
                                </button>
                                <ul className="dropdown-menu dropdown-menu-end">
                                    <li><Link className="dropdown-item" to="/dashboard">Dashboard</Link></li>
                                    <li><Link className="dropdown-item" to="/account">Account</Link></li>
                                    <li><hr className="dropdown-divider" /></li>
                                    <li><button onClick={handleLogout} className="dropdown-item">Logout</button></li>
                                </ul>
                            </li>
                        ) : (
                            // Se l'utente non è loggato, mostra il pulsante di accesso.
                            <li className="nav-item ms-lg-3">
                                <Link className="btn btn-primary" to="/login">Accedi</Link>
                            </li>
                        )}
                    </ul>
                </div>
            </div>
        </nav>
    );
}